import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { DollarSign, TrendingUp, ArrowLeftRight } from "lucide-react";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";
import { Pagination, paginate } from "@/components/Pagination";

export default function InvestorTransactions() {
  const { user } = useAuth();
  const [capital, setCapital] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [investorClasses, setInvestorClasses] = useState([]);

  const loadTransactions = useCallback(async () => {
    try {
      const { data: investors } = await API.get("/investors");
      if (investors.length > 0) {
        const inv = investors[0];
        setInvestorClasses(inv.classes || []);
        const { data } = await API.get(`/investors/${inv.id}/transactions`);
        setCapital(data.capital || []);
        setPerformance(data.performance || []);
      }
    } catch {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const totalSub = capital.reduce((s, t) => s + (t.subscription_amount || 0), 0);
  const totalRed = capital.reduce((s, t) => s + (t.redemption_amount || 0), 0);
  const totalIncome = performance.reduce((s, t) => s + (t.total_income || 0), 0);

  const { sorted: sortedCapital, sortKey: capSortKey, sortDir: capSortDir, toggleSort: toggleCapSort } = useSortableTable(capital);
  const { sorted: sortedPerformance, sortKey: perfSortKey, sortDir: perfSortDir, toggleSort: togglePerfSort } = useSortableTable(performance);

  const [capPage, setCapPage] = useState(1);
  const [perfPage, setPerfPage] = useState(1);
  const [capPageSize, setCapPageSize] = useState(50);
  const [perfPageSize, setPerfPageSize] = useState(50);

  const { paginatedItems: pagedCapital, totalPages: capTotalPages, safePage: capSafePage } = paginate(sortedCapital, capPage, capPageSize);
  const { paginatedItems: pagedPerformance, totalPages: perfTotalPages, safePage: perfSafePage } = paginate(sortedPerformance, perfPage, perfPageSize);

  const hasClasses = investorClasses.length > 1;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="investor-transactions-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">My Transactions</h1>
        <p className="text-muted-foreground text-sm mt-1">Your capital and performance transaction history</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="p-5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Net Subscriptions</span>
            <p className="font-heading text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">${totalSub.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </CardContent>
        </Card>
        <Card className="stat-card bg-red-500/10 border-red-500/20">
          <CardContent className="p-5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total Redemptions</span>
            <p className="font-heading text-2xl font-bold mt-1 text-red-600 dark:text-red-400">${totalRed.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </CardContent>
        </Card>
        <Card className="stat-card bg-blue-500/10 border-blue-500/20">
          <CardContent className="p-5">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total Income</span>
            <p className="font-heading text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">${totalIncome.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="capital" className="space-y-6">
        <TabsList className="bg-secondary/50 border border-border" data-testid="investor-txn-tabs">
          <TabsTrigger value="capital" className="gap-2"><DollarSign className="w-4 h-4" /> Capital</TabsTrigger>
          <TabsTrigger value="performance" className="gap-2"><TrendingUp className="w-4 h-4" /> Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="capital">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                    <SortableHead sortKey="date" currentKey={capSortKey} sortDir={capSortDir} onSort={toggleCapSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Date</SortableHead>
                    <SortableHead sortKey="subscription_amount" currentKey={capSortKey} sortDir={capSortDir} onSort={toggleCapSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right">Subscription</SortableHead>
                    <SortableHead sortKey="redemption_amount" currentKey={capSortKey} sortDir={capSortDir} onSort={toggleCapSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right">Redemption</SortableHead>
                    {hasClasses && <SortableHead sortKey="class_description" currentKey={capSortKey} sortDir={capSortDir} onSort={toggleCapSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Offering</SortableHead>}
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {capital.length === 0 ? (
                    <TableRow><TableCell colSpan={hasClasses ? 5 : 4} className="text-center py-12">
                      <DollarSign className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
                      <p className="text-muted-foreground text-sm">No capital transactions</p>
                    </TableCell></TableRow>
                  ) : pagedCapital.map(t => (
                    <TableRow key={t.id} data-testid={`inv-cap-${t.id}`}>
                      <TableCell className="pl-6 text-sm">{t.date}</TableCell>
                      <TableCell className="text-right text-sm text-emerald-400">{t.subscription_amount > 0 ? `$${t.subscription_amount.toLocaleString(undefined, {minimumFractionDigits: 2})}` : "—"}</TableCell>
                      <TableCell className="text-right text-sm text-red-400">{t.redemption_amount > 0 ? `$${t.redemption_amount.toLocaleString(undefined, {minimumFractionDigits: 2})}` : "—"}</TableCell>
                      {hasClasses && <TableCell className="text-sm text-muted-foreground">{t.class_description || "—"}</TableCell>}
                      <TableCell className="text-sm text-muted-foreground">{t.notes || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            <Pagination currentPage={capSafePage} totalPages={capTotalPages} onPageChange={setCapPage} pageSize={capPageSize} onPageSizeChange={(v) => { setCapPageSize(v); setCapPage(1); }} />
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                    <SortableHead sortKey="period_id" currentKey={perfSortKey} sortDir={perfSortDir} onSort={togglePerfSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold pl-6">Period</SortableHead>
                    {hasClasses && <TableHead className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Offering</TableHead>}
                    <SortableHead sortKey="total_income" currentKey={perfSortKey} sortDir={perfSortDir} onSort={togglePerfSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right">Total Income</SortableHead>
                    <SortableHead sortKey="management_fee" currentKey={perfSortKey} sortDir={perfSortDir} onSort={togglePerfSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right">Mgmt Fee</SortableHead>
                    <SortableHead sortKey="incentive_fee" currentKey={perfSortKey} sortDir={perfSortDir} onSort={togglePerfSort} className="text-xs uppercase tracking-wider text-muted-foreground font-bold text-right">Incentive Fee</SortableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {performance.length === 0 ? (
                    <TableRow><TableCell colSpan={hasClasses ? 5 : 4} className="text-center py-12">
                      <TrendingUp className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
                      <p className="text-muted-foreground text-sm">No performance transactions</p>
                    </TableCell></TableRow>
                  ) : pagedPerformance.map(t => (
                    <TableRow key={t.id} data-testid={`inv-perf-${t.id}`}>
                      <TableCell className="pl-6 text-sm font-medium">{t.period_id}</TableCell>
                      {hasClasses && <TableCell className="text-sm text-muted-foreground">{t.class_description || investorClasses.join(", ")}</TableCell>}
                      <TableCell className="text-right text-sm text-emerald-400">${(t.total_income||0).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell className="text-right text-sm text-amber-400">${(t.management_fee||0).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                      <TableCell className="text-right text-sm text-violet-400">${(t.incentive_fee||0).toLocaleString(undefined, {minimumFractionDigits: 2})}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            <Pagination currentPage={perfSafePage} totalPages={perfTotalPages} onPageChange={setPerfPage} pageSize={perfPageSize} onPageSizeChange={(v) => { setPerfPageSize(v); setPerfPage(1); }} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
